import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useStoreContext } from 'features/store';
import Animated, {
  FadeIn,
  LayoutAnimationConfig,
  SlideInLeft,
  SlideInRight,
} from 'react-native-reanimated';

import { ListCardGroup } from 'components/list-card-group';
import { ProductCard } from 'components/product-card';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { useMainMenuContext } from 'state/menu/main-menu';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { getEntryPrice, stringifySanityImage } from 'state/unified-menu/utils';

import { useProductWizardContext } from '../../product-wizard';
import { useProductWizardFlow } from '../../product-wizard/use-product-wizard-flow';
import { FADE_IN_ANIMATION_MILLIS, SLIDE_IN_ANIMATION_MILLIS, SelectorContainer } from '../styled';

import { ProductCustomizationDrinksTabs } from './product-customization-drinks-tabs';
import { ProductCustomizationModifiers } from './product-customization-modifiers';
import { DrinksTabsContent } from './styled';
import { CATEGORY_LABEL_KEY, CustomizationRenderItem, DrinkCategory } from './types';
import { getComboSlotItems, getDrinkCategory } from './utils';

export const ProductCustomizationDrinks = () => {
  const { indexedMenu } = useUnifiedMenuContext();
  const { currentFlowStep } = useProductWizardFlow();
  const { slotsSelections, selectComboSlotItem, removeSlotSelection } = useProductWizardContext();
  const menuQueryOptions = useMenuQueryOptions();
  const { formatCalories } = useMenuCalories();

  const [isMounted, setIsMounted] = useState<boolean>(false);
  const previousTabIndexRef = useRef<number>(0);

  const slotKey = currentFlowStep.slotKey!;
  const selectedSlot = slotsSelections[slotKey];

  const [selectedRow, setSelectedRow] = useState<number>();

  const { store } = useStoreContext();
  const { activeDayParts } = useMainMenuContext();
  const storeHasBurgersForBreakfast = Boolean(store.hasBurgersForBreakfast);

  const cachedComboDrinks: ILocalizedMenuEntry[] = useMemo(
    () =>
      getComboSlotItems({
        menu: indexedMenu,
        slotKey,
        posServiceMode: menuQueryOptions.posServiceMode,
        activeDayParts,
        storeHasBurgersForBreakfast,
      }),
    [
      indexedMenu,
      slotKey,
      menuQueryOptions.posServiceMode,
      activeDayParts,
      storeHasBurgersForBreakfast,
    ]
  );

  const availableCategories: DrinkCategory[] = useMemo(
    () =>
      cachedComboDrinks
        .reduce((categories, drink) => {
          const drinkCategory = getDrinkCategory(drink);
          if (!categories.includes(drinkCategory)) {
            categories.push(drinkCategory);
          }
          return categories;
        }, [] as DrinkCategory[])
        .sort(),
    [cachedComboDrinks]
  );

  const [activeTab, setActiveTab] = useState<DrinkCategory>(() => {
    const comboSlot = indexedMenu.find(currentFlowStep.slotKey);

    const selectedDrink =
      cachedComboDrinks.find(({ id }) => id === selectedSlot?.itemId) ||
      indexedMenu.find(comboSlot.options.defaults[0]);

    return selectedDrink ? getDrinkCategory(selectedDrink) : availableCategories[0];
  });

  const filteredDrinks = useCallback(
    () => cachedComboDrinks.filter(drink => getDrinkCategory(drink) === activeTab),
    [activeTab, cachedComboDrinks]
  );

  const enteringAnimation = useMemo(() => {
    if (!isMounted) {
      return;
    }

    const previousTabIndex = previousTabIndexRef.current;

    // // Determine if we are moving forward or backward
    if (activeTab > previousTabIndex) {
      return SlideInRight;
    } else if (activeTab < previousTabIndex) {
      return SlideInLeft;
    }

    // Default animation if we are at the same tab (no change)
    return SlideInRight;
  }, [activeTab, isMounted]);

  const handleDrinksTabChange = (tab: DrinkCategory) => {
    // Update the previous tab index ref before changing the state
    previousTabIndexRef.current = activeTab;
    setActiveTab(tab);

    logButtonClick({
      attributes: {
        component: ClickEventComponentNames.PRODUCT_DRINKS_CATEGORY_VIEW,
        name: CATEGORY_LABEL_KEY[tab]?.toString().toUpperCase() ?? '',
      },
    });
  };

  // LayoutAnimationConfig skipEntering logic for web devices
  useLayoutEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  return (
    <SelectorContainer>
      <ProductCustomizationDrinksTabs
        activeTab={activeTab}
        categories={availableCategories}
        onTabPress={activeTab => {
          removeSlotSelection(slotKey);
          handleDrinksTabChange(activeTab);
        }}
      />

      <DrinksTabsContent>
        <LayoutAnimationConfig skipEntering>
          <Animated.View
            key={`animation-${activeTab}`}
            entering={FadeIn.duration(FADE_IN_ANIMATION_MILLIS)}
          >
            <Animated.View entering={enteringAnimation?.duration(SLIDE_IN_ANIMATION_MILLIS)}>
              <ListCardGroup
                items={filteredDrinks()}
                key={`drink-list-${activeTab}`}
                selectedRow={selectedRow}
                ItemCustomizationComponent={<ProductCustomizationModifiers />}
                renderItem={({ item, index, rowIndex }: CustomizationRenderItem) => (
                  <ProductCard
                    key={`drink-${item.id}-${index}`}
                    isSelected={item.id === selectedSlot?.itemId}
                    productProps={{
                      name: item.name?.locale,
                      price: getEntryPrice(item, menuQueryOptions.posServiceMode),
                      calories: formatCalories(item.nutrition?.calories?.def ?? 0),
                      image: stringifySanityImage(item.image),
                    }}
                    onProductPress={() => {
                      logButtonClick({
                        attributes: {
                          component: ClickEventComponentNames.PRODUCT_COMBO_SLOT_SELECTION_UPDATE,
                          name: item.name.locale ?? '',
                          Id: item.id ?? '',
                        },
                      });
                      selectComboSlotItem(slotKey, item.id);
                      setSelectedRow(rowIndex);
                    }}
                  />
                )}
              />
            </Animated.View>
          </Animated.View>
        </LayoutAnimationConfig>
      </DrinksTabsContent>
    </SelectorContainer>
  );
};
